import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { fileNameMismatches, useDocuments } from '../lib/documents.js';
import { usePeople } from '../lib/people.js';
import { queryKeys } from '../lib/queryClient.js';

/**
 * Settings control that tidies stored file names (Settings →
 * Documents & AI). Files uploaded since the metadata-derived naming
 * rule are named correctly at upload and kept in sync on every save;
 * this control catches the back catalogue — files still wearing their
 * camera-roll names. It counts mismatches with the same shared deriver
 * the server uses, and "renaming" is simply an empty PATCH per
 * document: the server re-derives the name and re-files the Dropbox
 * mirror in its usual save pass. Self-healing: once everything
 * matches, the control reports exactly that.
 */
export function FileNamesSection() {
  const documents = useDocuments();
  const people = usePeople();
  const qc = useQueryClient();
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [failed, setFailed] = useState(0);

  const peopleById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of people.data ?? []) map.set(p.id, p.displayName);
    return map;
  }, [people.data]);

  const mismatched = useMemo(
    () => fileNameMismatches(documents.data ?? [], peopleById),
    [documents.data, peopleById],
  );

  async function tidy() {
    setRunning(true);
    setProgress(0);
    setFailed(0);
    let failures = 0;
    // Sequential on purpose: each rename also moves the Dropbox mirror,
    // and a gentle one-at-a-time pass keeps within its rate limits.
    for (const [index, doc] of mismatched.entries()) {
      setProgress(index + 1);
      try {
        await api.updateDocument(doc.id, {});
      } catch {
        failures += 1;
      }
    }
    setFailed(failures);
    await qc.invalidateQueries({ queryKey: queryKeys.documents });
    setRunning(false);
  }

  const count = mismatched.length;

  return (
    <div className="home-layout">
      <span className="reminder-toggle__heading">File names</span>
      <p className="home-layout__hint">
        Stored files are named after their records — &ldquo;2026-06-22 Pediatrician
        Invoice.pdf&rdquo;, never &ldquo;img20260706.pdf&rdquo; — and renamed whenever a record
        changes. This tidies files from before that rule.
      </p>
      {documents.isSuccess && count === 0 && !running && (
        <p className="home-layout__hint">
          {failed > 0
            ? `${failed} ${failed === 1 ? 'file' : 'files'} could not be renamed — try again later.`
            : 'All file names match their records.'}
        </p>
      )}
      {(count > 0 || running) && (
        <div>
          <button type="button" className="btn btn--secondary btn--small" onClick={tidy} disabled={running}>
            {running
              ? `Renaming ${progress} of ${count}…`
              : `Rename ${count} ${count === 1 ? 'file' : 'files'}`}
          </button>
        </div>
      )}
    </div>
  );
}
